import React, { useEffect, useRef, useCallback, useState } from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import {
  Row, Col, Button, DropdownButton, ButtonGroup,
  Dropdown, Container, OverlayTrigger, Tooltip, Image,
} from 'react-bootstrap';
import { IndexLinkContainer } from 'react-router-bootstrap';
import { withRouter, matchPath } from 'react-router-dom';
import { Icon, Breadcrumbs } from '@blueprintjs/core';
import { useTranslation } from 'react-i18next';

import { movus } from '../constants';
import useDomain from '../modules/domain/hooks/useDomain';

import BasicTooltip from './BasicTooltip';

import useAlarmDowntimeSaved from '../modules/alarm/hooks/useAlarmDowntimeSaved';
import NotificationCenter from '../modules/notification/components/NotificationCenter';
import { LanguageSwitcher } from './Internationalisation';

import './header.scss';

import {
  IoIosContact,
  IoIosHome,
  IoIosMap,
  IoIosNotifications,
  IoIosRadio,
  IoIosLaptop,
  IoIosLogOut,
  IoIosMenu,
  IoIosPersonAdd,
  IoLogoUsd,
  IoIosPaper,
  IoIosTrendingUp,
  IoIosTimer,
} from "react-icons/io";

import IoIosFactory from './IoIosFactory';
import IoIosMotor from './IoIosMotor';

import { logout, submitUserPreferences, fetchUserPreferences } from '../modules/user/actions';
import Private from './Private';
import {
  getUser,
  getUserTag,
  isSuperAdmin,
  isUserLoggedIn
} from '../modules/user/selectors';
import {
  getActiveGroupId,
  getGroupTreePath,
  getOrganisationGroup,
  getCurrentOrganisationHasProductCode,
  getOrganisationType,
} from '../modules/organisation/selectors';
import { headerHeight } from '../modules/app/actions';
import { usePoll } from '../hooks/usePoll';
import { getIntervalMilliSeconds } from './lib/utils';
import useHomeView, { HOME_VIEW } from '../hooks/useHomeView';
import { debounce, downtimeSavedIsHours } from '../lib/utils';

const blueprintIconStyle = {
  width: '1.2em',
  marginRight: '0.3em',
  textAlign: 'center',
};

const { REACT_APP_CHECK_USER_INTERVAL_MINUTES = 30 } = process.env;
const INTERVAL = getIntervalMilliSeconds(REACT_APP_CHECK_USER_INTERVAL_MINUTES);

function HeaderButton({ tooltipText, to, Icon, children, matchTo, ...buttonProps }) {
  const LinkContainer = to ? IndexLinkContainer : 'div';

  // match beginning of path
  const getIsActive = useCallback((match, { pathname='' }={}) => {
    return matchPath(pathname, { path: matchTo });
  }, [matchPath, matchTo]);

  // create consistently styled buttons
  const button = (
    <LinkContainer {...(to ? { to, isActive: getIsActive} : {})}>
      <Button size="sm" variant="outline-secondary" {...buttonProps}>
        {Icon && (
          <Icon size="2em" />
        )} {children && (
          <span>
            {children}
          </span>
        )}
      </Button>
    </LinkContainer>
  );

  // display button inside a tooltip trigger or not
  return !tooltipText ? button : (
    <OverlayTrigger
      placement="bottom"
      overlay={(
        <Tooltip>
          {tooltipText}
        </Tooltip>
      )}
    >
      {button}
    </OverlayTrigger>
  );
}

function HeaderBreadcrumbs() {
  const activeGroupId = useSelector(getActiveGroupId);
  const organisationGroup = useSelector(getOrganisationGroup, shallowEqual);
  // If there's group selected, show the group's path; otherwise(no group selected, i.e. user just logged in), show the top group name.
  const treePath = useSelector(state => getGroupTreePath(state, activeGroupId), shallowEqual) || (organisationGroup ? [organisationGroup] : []);

  return (
    <Breadcrumbs items={
      treePath.map(item => ({ text: item.name }))
    } />
  );
}

function UserHeader(props) {

  const { t } = useTranslation();
  const {
    userIsLoggedIn,
    user,
    expanded,
    toggleExpanded,
    hasEquipmentMapFeature,
    hasNetworkFeature,
    hasOrganisationTokenFeature,
    hasBillingCentre,
    hasSelectedableDash,
    hasPlantOs,
    headerHeight,
    fetchUserPreferences,
    isSuperAdmin,
    organisationType,
  } = props;
  const headerRef = useRef(null);
  const { view, setView } = useHomeView();  // @TODO: Refactor to useLocalPreference hook.
  const { orgAlarmDowntimeSaved, fetchOrgDowntimeSaved } = useAlarmDowntimeSaved();
  const { domainData, fetchPlantOs } = useDomain({ key: 'plantos', fetch: true, cache: false });
  const [plantOsNinePin, setPlantOsNinePin] = useState(null);

  useEffect(() => {
    const handleHeaderHeight = debounce(() => {
      headerHeight(headerRef.current?.offsetHeight || 0);
    }, 500);
    window.addEventListener('resize', handleHeaderHeight);
    return () => {
      window.removeEventListener('resize', handleHeaderHeight);
    };
  }, []);

  useEffect(() => {
    fetchOrgDowntimeSaved();
  }, []);

  useEffect(() => {
    fetchPlantOs();
  }, []);

  useEffect(() => {
    if (domainData) {
      setPlantOsNinePin(domainData);
    }
  }, [domainData]);

  usePoll(() => {
    fetchUserPreferences(user, { isUserAction: false });
  }, [], { interval: INTERVAL, skipFirstTime: true });

  if (!userIsLoggedIn) {
    return null;
  }
  return (
    <div ref={headerRef}>
      <Container fluid>
        <Row className="small-gutters mt-1 pt-1 mb-1">
          <Col xs="auto">
            <Row className="small-gutters">
              <Col className={`mb-1 ${expanded !== false ? 'd-md-none' : ''}`} xs="auto">
                <HeaderButton
                  tooltipText={!expanded ? t('header.views') : ''}
                  Icon={IoIosMenu}
                  onClick={toggleExpanded}
                />
              </Col>
              <Col className="mb-1" xs="auto">
                <ButtonGroup>
                  <HeaderButton
                    tooltipText={t('header.home')}
                    Icon={IoIosHome}
                    to="/home"
                    matchTo="/home"
                  />
                  {hasSelectedableDash && <DropdownButton as={ButtonGroup} menuAlign="left" variant="outline-secondary" title="" size="sm">
                    <Dropdown.Header>{t('header.choose-a-view')}</Dropdown.Header>
                    <IndexLinkContainer to="/home" isActive={(match) => match && view===HOME_VIEW.STANDARD}>
                      <Dropdown.Item onClick={() => setView(HOME_VIEW.STANDARD)}>
                        {t('header.standard')}
                      </Dropdown.Item>
                    </IndexLinkContainer>
                    {hasSelectedableDash && <>
                      <IndexLinkContainer to="/home" isActive={(match) => match && view===HOME_VIEW.PLANT}>
                        <Dropdown.Item onClick={() => setView(HOME_VIEW.PLANT)}>
                          {t('header.plant-room')}
                        </Dropdown.Item>
                      </IndexLinkContainer>
                      <IndexLinkContainer to="/home" isActive={(match) => match && view===HOME_VIEW.MANAGEMENT}>
                        <Dropdown.Item onClick={() => setView(HOME_VIEW.MANAGEMENT)}>
                          {t('header.management')}
                        </Dropdown.Item>
                      </IndexLinkContainer>
                    </>}
                  </DropdownButton>}
                </ButtonGroup>
              </Col>
              <Col className="mb-1" xs="auto">
                <ButtonGroup size="sm" data-toggle="buttons">
                  {hasEquipmentMapFeature && (
                    <HeaderButton
                      tooltipText={t('header.equipment-map')}
                      Icon={IoIosMap}
                      to="/equipment/map"
                      matchTo="/equipment/map"
                    />
                  )}
                  <HeaderButton
                    tooltipText={t('common.equipment-list')}
                    Icon={IoIosMotor}
                    to="/equipment/list"
                    matchTo="/equipment/list"
                  />
                  <HeaderButton
                    tooltipText={t('header.alarms')}
                    Icon={IoIosNotifications}
                    to="/alarms/list/condition"
                    matchTo="/alarms/list/:alarmType"
                  />
                  <HeaderButton
                    tooltipText={t('header.events')}
                    Icon={IoIosPaper}
                    to="/events"
                    matchTo="/events"
                  />
                  {isSuperAdmin && (<HeaderButton
                    tooltipText={t('header.plant-visibility-timeline')}
                    Icon={IoIosTrendingUp}
                    to="/plantvisibility/timeline"
                    matchTo="/plantvisibility/timeline"
                  />)}
                </ButtonGroup>
              </Col>
            </Row>
          </Col>
          {/*  when this column overflows, ensure is is right-aligned using ml-auto */}
          <Col className="ml-auto" xs="auto">
            {/* align items in this row to the right */}
            <Row className="small-gutters justify-content-end">
              {/* Display only for non MOVUS domain, regardless of being Super Admin or not */}
              {organisationType !== movus && <Col className="mb-1" xs="auto">
                <div
                  className="btn btn-outline-secondary btn-sm"
                >
                  <>
                    <IoIosTimer size="2em" />&nbsp;
                    <BasicTooltip
                      text={t('header.total-hours-of-downtime-saved-tooltip')}
                      placement="bottom"
                    >
                      <span>
                        <strong>
                          {downtimeSavedIsHours(orgAlarmDowntimeSaved) ? parseInt(orgAlarmDowntimeSaved.hours) : '#'}
                          &nbsp;
                        </strong>
                        {/*
                          The text here is "Total hours" so we don't need to use the i18n library
                          to decide between hour and hours even if set to 1.
                        */}
                        {t('header.total-hours-of-downtime-saved-text')}
                      </span>
                    </BasicTooltip>
                  </>
                </div>
              </Col>}
              <Private minUserType="Admin">
                <Col className="mb-1" xs="auto">
                  <HeaderButton
                    to="/users/new"
                    Icon={IoIosPersonAdd}
                  >
                    {t('header.invite-user')}
                  </HeaderButton>
                </Col>
              </Private>
              {hasPlantOs && plantOsNinePin && <Col className="mb-1" xs="auto">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="md"
                  >
                    {plantOsNinePin?.dropdown_title || 'PlantOS'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      LinkComponent={Button}
                      LinkComponentAs="a"
                      onClick={() => window.open(plantOsNinePin.main_title_url, '_blank')}
                      style={{
                        width: '400px',
                        height: '50px',
                        lineHeight: '50px',
                        fontSize: '1.2em',
                        fontWeight: 'bold',
                        color: 'white',
                        backgroundColor: '#018ad9',
                      }}
                    >
                      {plantOsNinePin.main_title}
                    </Dropdown.Item>
                    {plantOsNinePin.sections.map((section) =>
                      <>
                        <Dropdown.Header style={{
                          fontSize: '1em',
                          fontWeight: 'bold',
                          color: 'black',
                        }}>
                          {section.section_title}
                        </Dropdown.Header>
                        {
                          section.buttons.map((button) =>
                            <>
                              <Col>
                                <Dropdown.Item
                                  onClick={() => window.open(button.button_url, '_blank')}
                                  style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '10px',
                                    width: '220px',
                                    textAlign: 'center',
                                    overflowWrap: 'break-all',
                                  }}
                                >
                                  <div>
                                    <Image
                                      src={button.icon}
                                      style={{ height: '1.4em' }}
                                      alt={button.icon_alt_text}
                                    />
                                  </div>
                                  <div>
                                    <span style={{ fontSize: '.9em' }}>{button.button_text}</span>
                                  </div>
                                </Dropdown.Item>
                              &nbsp;
                              </Col>
                            </>
                          )
                        }
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>}
              <Col className="mb-1" xs="auto">
                <ButtonGroup>
                  <HeaderButton to="/users/me" Icon={IoIosContact}>
                    {t('header.me')}
                  </HeaderButton>
                  <DropdownButton as={ButtonGroup} menuAlign="right" variant="outline-secondary" title="">
                    <Private minUserType="Admin">
                      <Dropdown.Header>{t('header.admin.admin-tools')}</Dropdown.Header>
                      <IndexLinkContainer to="/devices/admin">
                        <Dropdown.Item>
                          <IoIosRadio size="1.2em" /> <span>{t('header.admin.sensor-admin')}</span>
                        </Dropdown.Item>
                      </IndexLinkContainer>
                      {hasNetworkFeature && (
                        <IndexLinkContainer to="/gateways/admin">
                          <Dropdown.Item>
                            <Icon
                              iconSize="1em"
                              icon="graph"
                              title="Network"
                              style={blueprintIconStyle}
                            /> <span>{t('header.admin.network')}</span>
                          </Dropdown.Item>
                        </IndexLinkContainer>
                      )}
                      <IndexLinkContainer to="/users/admin">
                        <Dropdown.Item>
                          <Icon
                            iconSize="1em"
                            icon="people"
                            title="Users"
                            style={blueprintIconStyle}
                          /> <span>{t('header.admin.users')}</span>
                        </Dropdown.Item>
                      </IndexLinkContainer>
                      <IndexLinkContainer to="/organisations/admin">
                        <Dropdown.Item>
                          <IoIosFactory size="1.2em" /> <span>{t('header.admin.organisations')}</span>
                        </Dropdown.Item>
                      </IndexLinkContainer>
                      {hasOrganisationTokenFeature && (
                        <IndexLinkContainer to="/developer/admin/tokens">
                          <Dropdown.Item>
                            <IoIosLaptop size="1.2em" /> <span>{t('header.admin.developers')}</span>
                          </Dropdown.Item>
                        </IndexLinkContainer>
                      )}
                      {hasBillingCentre && (
                        <IndexLinkContainer to="/billing">
                          <Dropdown.Item>
                            <IoLogoUsd size="1.2em" /> <span>{t('header.admin.billing')}</span>
                          </Dropdown.Item>
                        </IndexLinkContainer>
                      )}
                      <Dropdown.Divider />
                    </Private>
                    <Dropdown.Item onClick={() => props.logout()}>
                      <IoIosLogOut size="1.2em" /> <span>{t('header.logout')}</span>
                    </Dropdown.Item>
                  </DropdownButton>
                </ButtonGroup>
              </Col>
              <Col className="mb-1" xs="auto">
                <NotificationCenter />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="small-gutters mt-1 pt-1 mb-1">
          <HeaderBreadcrumbs />
        </Row>
      </Container>
    </div>
  );
}

function PublicHeader() {
  return (
    <Container fluid>
      <Row className="small-gutters justify-content-end small-gutters mt-1 pt-1 mb-1">
        <Col className="mb-1" xs="auto">
          <LanguageSwitcher />
        </Col>
      </Row>
    </Container>
  );
}

function Header(props) {
  const { userIsLoggedIn } = props;
  // show the header only if the user is logged in
  return userIsLoggedIn ? (
    <UserHeader {...props} />
  ) : <PublicHeader />;
}

const mapStateToProps = state => {
  return {
    userIsLoggedIn: isUserLoggedIn(state),
    user: getUser(state),
    whatsNewHighlight: getUserTag(state, 'whats_new_highlight'),
    hasEquipmentMapFeature: getCurrentOrganisationHasProductCode(state, 'map_views'),
    hasNetworkFeature: getCurrentOrganisationHasProductCode(state, 'network_centre'),
    hasOrganisationTokenFeature: getCurrentOrganisationHasProductCode(state, 'token_api'),
    hasBillingCentre: getCurrentOrganisationHasProductCode(state, 'billing_centre'),
    hasSelectedableDash: getCurrentOrganisationHasProductCode(state, 'select_dash'),
    hasFitPower: getCurrentOrganisationHasProductCode(state, 'fitpower'),
    hasPlantOs: getCurrentOrganisationHasProductCode(state, 'plantos'),
    isSuperAdmin: isSuperAdmin(state),
    organisationType: getOrganisationType(state),
  };
};

const mapDispatchToProps = {
  logout,
  headerHeight,
  submitUserPreferences,
  fetchUserPreferences,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
